import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import { EffortLevel } from "./effortLevel.js";
import { ModelFeatures } from "./ModelFeatures.js";

/**
 * Effort tiers accepted by OpenAI-compatible reasoning APIs. Support is
 * model-specific; most OpenAI models top out at `xhigh`.
 */
export const OpenAiReasoningEffortLiteral = Union(
  Literal("none"),
  Literal("minimal"),
  Literal("low"),
  Literal("medium"),
  Literal("high"),
  Literal("xhigh"),
  Literal("max"),
);
export type OpenAiReasoningEffort = Static<typeof OpenAiReasoningEffortLiteral>;

export const OpenAiFastServiceTierLiteral = Union(
  Literal("fast"),
  Literal("priority"),
);
export type OpenAiFastServiceTier = Static<typeof OpenAiFastServiceTierLiteral>;

/** Canonical {@link EffortLevel} → OpenAI API param; `max` collapses to `xhigh`. */
export function toOpenAi(p: EffortLevel): OpenAiReasoningEffort {
  return p === "max" ? "xhigh" : p;
}

/** @deprecated */
export const DeprecatedOpenAiChatModelLiteral = Union(
  Literal("ULTIMA_ALPHA"),
  Literal("GPT_4_OMNI"),
  Literal("GPT_4_OMNI_MINI"),
  Literal("GPT_41"),
  Literal("GPT_5"),
  Literal("GPT_51"),
  Literal("GPT_53_CODEX"),
  Literal("GPT_NEXT_V2_AUG_2026"),
  Literal("O3"),
  Literal("GPT_41_MINI"),
  Literal("GPT_54_NANO"),
  Literal("GPT_41_NANO"),
  Literal("GPT_52"),
  Literal("CREST_ALPHA"),
  Literal("JOULE_ALPHA"),
  Literal("FIREFLY_ALPHA"),
);
/** @deprecated */
export type DeprecatedOpenAiChatModel = Static<
  typeof DeprecatedOpenAiChatModelLiteral
>;
/** @deprecated */
export const DeprecatedOpenAiChatModel = getNormalEnum(
  DeprecatedOpenAiChatModelLiteral,
);

export const OpenAiChatModelLiteral = Union(
  Literal("GPT_54"),
  Literal("GPT_54_MINI"),
  Literal("GPT_55"),
  Literal("GPT_56"),
  Literal("GPT_56_TERRA"),
  Literal("GPT_56_LUNA"),
  Literal("GPT_6_ASTRA"),
  Literal("GPT_6_SOL"),
  Literal("GPT_6_LUNA"),
);
export type OpenAiChatModel = Static<typeof OpenAiChatModelLiteral>;
export const OpenAiChatModel = getNormalEnum(OpenAiChatModelLiteral);

type OpenAiChatModelFeatures = ModelFeatures & {
  // OpenAI documentation of how they count tokens seems to be kinda off, so
  // for now we'll just subtract 10 from the max tokens to be safe.
  maxInputTokens: number;
  maxOutputTokens: number;
  tokensPerMessage: number;
  /** Accepts `none` reasoning. */
  noneEffort?: true;
  /** Accepts `minimal` reasoning (OpenAI added this in 5.1). */
  minimalEffort?: true;
  /** Accepts `xhigh` reasoning. */
  xHighEffort?: true;
  /** Accepts `max` reasoning. */
  maxEffort?: true;
};

export const OpenAiChatModelFeatureMap: Record<
  OpenAiChatModel,
  OpenAiChatModelFeatures
> = {
  [OpenAiChatModel.GPT_54]: {
    name: "gpt-5.4",
    label: "GPT 5.4",
    requiresDataRetention: false,
    maxInputTokens: 1000000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_54_MINI]: {
    name: "gpt-5.4-mini",
    label: "GPT 5.4 Mini",
    requiresDataRetention: false,
    maxInputTokens: 400000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_55]: {
    name: "gpt-5.5",
    label: "GPT 5.5",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    minimalEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_56]: {
    name: "gpt-5.6",
    label: "GPT 5.6 Sol",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_56_TERRA]: {
    name: "gpt-5.6-terra",
    label: "GPT 5.6 Terra",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_56_LUNA]: {
    name: "gpt-5.6-luna",
    label: "GPT 5.6 Luna",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_6_ASTRA]: {
    name: "gpt-6-astra",
    label: "GPT 6 Astra",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_6_SOL]: {
    name: "gpt-6-sol",
    label: "GPT 6 Sol",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_6_LUNA]: {
    name: "gpt-6-luna",
    label: "GPT 6 Luna",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
  },
};

/**
 * Assistant phase metadata from the OpenAI Responses API.
 */
export const OpenAIAssistantPhaseLiteral = Union(
  Literal("commentary"),
  Literal("final_answer"),
);
export type OpenAIAssistantPhase = Static<typeof OpenAIAssistantPhaseLiteral>;
export const isOpenAIAssistantPhase = (
  phase: unknown,
): phase is OpenAIAssistantPhase =>
  phase === "commentary" || phase === "final_answer";

// These params are not allowed to be set for reasoning models
// The API will return a 400 if they are
export const openAiReasoningModelChatCompletionParams = {
  temperature: undefined,
  top_p: undefined,
  parallel_tool_calls: undefined,
};

export const OpenAiModelParams = RRecord({
  frequencyPenalty: Number,
  n: Number,
  presencePenalty: Number,
  stopWords: Array(String),
  temperature: Optional(Number), // Not used by reasoning models
  topP: Optional(Number), // Not used by reasoning models
  reasoningEffort: Optional(OpenAiReasoningEffortLiteral), // ONLY used by reasoning models
  serviceTier: Optional(OpenAiFastServiceTierLiteral),
  verbosity: Optional(
    Union(Literal("low"), Literal("medium"), Literal("high")),
  ),
  model: OpenAiChatModelLiteral,
  toolSearch: Optional(
    RRecord({
      enabled: Boolean,
      /** Tool names that should never be deferred (always loaded into context) */
      alwaysLoadedTools: Optional(Array(String)),
    }),
  ),
});
export type OpenAiModelParams = Static<typeof OpenAiModelParams>;

/**
 * OpenAI chat models that support Fast mode (`serviceTier: "fast" |
 * "priority"`). Adding a model here is the single allowlist; billable-model
 * mapping in `getBillableModel` is typed against this list so new Fast models
 * require a corresponding billable-model mapping.
 */
export const OPENAI_FAST_MODE_CHAT_MODELS = [
  OpenAiChatModel.GPT_56,
  OpenAiChatModel.GPT_6_LUNA,
  OpenAiChatModel.GPT_6_ASTRA,
  OpenAiChatModel.GPT_6_SOL,
] as const satisfies readonly OpenAiChatModel[];

export type OpenAiFastModeChatModel =
  (typeof OPENAI_FAST_MODE_CHAT_MODELS)[number];

export const isOpenAiFastModeChatModel = (
  model: OpenAiModelParams["model"],
): model is OpenAiFastModeChatModel =>
  OPENAI_FAST_MODE_CHAT_MODELS.some((fastModel) => fastModel === model);

export const isOpenAiFastModeParams = (
  modelParams: OpenAiModelParams,
): modelParams is Omit<OpenAiModelParams, "model"> & {
  model: OpenAiFastModeChatModel;
} =>
  isOpenAiFastModeChatModel(modelParams.model) &&
  OpenAiFastServiceTierLiteral.guard(modelParams.serviceTier);

/**
 * Maps the requested OpenAI effort tier to the value to send, clamping tiers
 * the target model doesn't support down to the next-best supported one.
 */
export function buildOpenAiEffortForRequest(
  modelParams: OpenAiModelParams,
): OpenAiReasoningEffort | null {
  const effort = modelParams.reasoningEffort ?? null;
  if (effort == null) return null;
  const features = OpenAiChatModelFeatureMap[modelParams.model];
  if (effort === "none" && !features.noneEffort) return "low";
  if (effort === "minimal" && !features.minimalEffort) return "low";
  if (effort === "xhigh" && !features.xHighEffort) return "high";
  if (effort === "max" && !features.maxEffort) {
    return features.xHighEffort ? "xhigh" : "high";
  }
  return effort;
}

export const OpenAiToolSearchConfig = RRecord({
  enabled: Boolean,
  /** Tool names that should never be deferred (always loaded into context) */
  alwaysLoadedTools: Optional(Array(String)),
});
export type OpenAiToolSearchConfig = Static<typeof OpenAiToolSearchConfig>;

export const OpenAiCompactionConfig = RRecord({
  enabled: Boolean,
  compactThreshold: Optional(Number),
});
export type OpenAiCompactionConfig = Static<typeof OpenAiCompactionConfig>;

export const OpenAiDefaultCompactionConfig: OpenAiCompactionConfig = {
  enabled: true,
  compactThreshold: 300000,
};

// Based on `ChatCompletionRequestMessageRoleEnum` from openai package
export const OpenAiChatMessageRoleLiteral = Union(
  Literal("system"),
  Literal("user"),
  Literal("assistant"),
  Literal("tool"),
);
export type OpenAiChatMessageRole = Static<typeof OpenAiChatMessageRoleLiteral>;
export const OpenAiChatMessageRole = getNormalEnum(
  OpenAiChatMessageRoleLiteral,
);

// based on `ChatCompletionContentPartImage` from openai package
const OpenAiChatContentPartImage = RRecord({
  type: Literal("image_url"),
  image_url: RRecord({
    url: String,
  }),
});

// based on `ChatCompletionContentPartFile` from openai package
// See https://developers.openai.com/api/docs/guides/pdf-files/
const OpenAiChatContentPartFile = RRecord({
  type: Literal("file"),
  file: RRecord({
    filename: String,
    file_data: String, // base64-encoded file with data URI prefix
  }),
});

const OpenAiChatContentPart = Union(
  OpenAiChatContentPartImage,
  OpenAiChatContentPartFile,
);

// Based on `ChatCompletionRequestMessage` from openai package
export const OpenAiChatMessage = Union(
  RRecord({
    content: String,
    role: Literal(OpenAiChatMessageRole.system),
  }),
  RRecord({
    content: String.Or(Array(OpenAiChatContentPart)),
    role: Literal(OpenAiChatMessageRole.user),
  }),
  RRecord({
    content: String.Or(Null),
    // OpenAI requires snake case
    function_call: Optional(RRecord({ arguments: String, name: String })),
    reasoning_content: Optional(String),
    role: Literal(OpenAiChatMessageRole.assistant),
  }),
  RRecord({
    content: String,
    role: Literal(OpenAiChatMessageRole.tool),
    tool_call_id: String,
  }),
  RRecord({
    content: Optional(String.Or(Null)),
    reasoning_content: Optional(String),
    tool_calls: Array(
      RRecord({
        type: Literal("function"),
        id: String,
        function: RRecord({ name: String, arguments: String }),
      }),
    ),
    role: Literal(OpenAiChatMessageRole.assistant),
  }),
);
export type OpenAiChatMessage = Static<typeof OpenAiChatMessage>;

export const OpenAIConformerResult = RRecord({
  messages: Array(OpenAiChatMessage),
});
export type OpenAIConformerResult = Static<typeof OpenAIConformerResult>;
